import { createTheme, ThemeOptions, } from '@mui/material/styles'
import { colors } from './colors'
import { HandleTheme, mode } from './types'
import '../../font/fonts.css'
import { components } from './componentsConfig'
import { PaletteColorOptions } from '@mui/material'
import { useState } from 'react'
import { Color } from './types/color'
import { FontName } from '../enums/font'

declare module '@mui/material/LinearProgress' {
  interface LinearProgressProps {
    customesecondcolor?: string
    customecolor?: string
  }
}
declare module '@mui/material/Tooltip' {
  interface TooltipProps {
    remove?: boolean
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    header_card: React.CSSProperties;
    title: React.CSSProperties;
    title_grey: React.CSSProperties;
    title_grey_dark: React.CSSProperties;
    title_grey_big: React.CSSProperties;
    simple_text?: React.CSSProperties;
    text?: React.CSSProperties;
    footer: React.CSSProperties;
    title_main: React.CSSProperties;
    title_blue: React.CSSProperties;
    subtitle: React.CSSProperties;
    subtitle_grey: React.CSSProperties;
    no_comment: React.CSSProperties;
    subtitle_blue: React.CSSProperties;
    text_grey: React.CSSProperties;
    footer_grey: React.CSSProperties
    no_data: React.CSSProperties;
    mediumBold: React.CSSProperties;
    blue_title: React.CSSProperties;
    big: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    header_card?: React.CSSProperties;
    title?: React.CSSProperties;
    text?: React.CSSProperties;
    footer?: React.CSSProperties;
    title_main?: React.CSSProperties;
    title_blue?: React.CSSProperties;
    subtitle?: React.CSSProperties;
    subtitle_grey?: React.CSSProperties;
    no_comment?: React.CSSProperties;
    subtitle_blue?: React.CSSProperties;
    text_grey?: React.CSSProperties;
    footer_grey: React.CSSProperties
    no_data?: React.CSSProperties;
    big: React.CSSProperties
  }


  interface PaletteOptions {
    white: string;
    blue: PaletteColorOptions;
    c_grey: PaletteColorOptions;
    c_red: PaletteColorOptions;
    c_green: PaletteColorOptions;
    c_yellow: PaletteColorOptions;
    c_orange: PaletteColorOptions;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    header_card?: React.CSSProperties;
    title?: React.CSSProperties;
    simple_text?: React.CSSProperties;
    title_grey?: React.CSSProperties;
    title_grey_dark?: React.CSSProperties;
    title_grey_big?: React.CSSProperties;
    footer?: React.CSSProperties;
    title_main?: React.CSSProperties;
    title_blue?: React.CSSProperties;
    subtitle?: React.CSSProperties;
    no_data?: React.CSSProperties;
    mediumBold?: React.CSSProperties;
    blue_title?: React.CSSProperties;
  }

  interface PaletteColor {
    primary?: PaletteColorOptions;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    header_card: true;
    title: true;
    title_grey: true;
    title_grey_dark: true;
    title_grey_big: true;
    text: true;
    footer: true;
    title_main: true
    title_blue: true
    subtitle: true
    subtitle_grey: true
    no_comment: true
    subtitle_blue: true
    text_grey: true
    footer_grey: true
    no_data: true
    mediumBold: true
    blue_title: true
    simple_text: true
    big: true
  }
}
declare module '@mui/material/Icon' {
  interface IconPropsColorOverrides {
    primary_company: true;
    grey: true;
    red: true;
    white: true;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    container_transparent: true;
    container_error: true;
    container_error_outlined: true;
    primary_c: true;
    primary_c_break: true;
    primary_c_nr: true;
    dark_c_nr: true;
    blue_c_nr: true;
    text_primary: true;
    text_primary_small: true;
  }
}

export const useTheme = (mode: mode = 'light') => {
  const [color, setColors] = useState<Color>(colors(mode))
  const [defaultTheme, setDefaultTheme] = useState(createTheme({
    palette: {
      primary: {
        main: color.color16,
        "100": color.primary
      },
      secondary: {
        main: color.color4,
        100: color.color1
      },
      white: color.color5,
      blue: {
        main: color.color1,
        "100": color.color14,
        "200": color.ashBlue,
      },
      c_grey: {
        main: color.color22,
        100: color.color13,
        200: color.color20,
        300: color.color9,
      },
      c_red: {
        main: color.color12,
        "100": 'rgba(204, 0, 0, 0.2)',
        '200': 'rgb(204, 0, 0)',
        '300': 'rgb(255,0,0, 0.06)',
        '400': color.color19
      },
      c_green: {
        main: color.color8,
        100: '#31C46222',
      },
      c_yellow: {
        main: color.secondary
      },
      c_orange: {
        main: color.color29
      }
    },
    components: {
      MuiDrawer: {
        styleOverrides: {
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)'
            },
          },
        }
      },
      ...components(color),
      MuiLinearProgress: {
        defaultProps: {
          customesecondcolor: "",
          customecolor: "",
        }
      },
      MuiIcon: {
        variants: [
          {
            props: { color: 'primary_company' },
            style: {
              color: color.primary
            },
          },
          {
            props: { color: 'grey' },
            style: {
              color: color.color13
            },
          },
          {
            props: { color: 'red' },
            style: {
              color: color.color12
            },
          },
          {
            props: { color: 'white' },
            style: {
              color: color.color5
            },
          },
        ]
      },
    },
    typography: {
      header_card: {
        fontSize: '10px',
        color: color.color22
      },
      big: {
        fontSize: '24px',
        color: color.color22
      },
      title: {
        fontSize: '20px',
        color: color.color22
      },
      title_main: {
        fontSize: '16px',
        color: color.color22,
        fontWeight: 'bold'
      },
      title_blue: {
        fontSize: '20px',
        color: color.secondaryEndpoint,
        fontWeight: 'bold',
        fontFamily: FontName.RobotoRegular,
      },
      title_grey: {
        fontSize: '18px',
        color: color.color13,
        fontWeight: 'bold',
        fontFamily: FontName.RobotoRegular,
      },
      title_grey_dark: {
        fontSize: '18px',
        color: color.color22,
        fontWeight: 'bold',
        fontFamily: FontName.RobotoRegular,
      },
      title_grey_big: {
        fontSize: '20px',
        color: color.color13,
        fontWeight: 'bold',
        textAlign: 'start',
        fontFamily: FontName.RobotoRegular,
      },
      subtitle: {
        fontSize: '16px',
        color: color.color22,
        wordBreak: 'break-word'
      },
      simple_text: {
        fontSize: '14px',
        color: color.color22
      },
      subtitle_grey: {
        fontSize: '16px',
        color: color.color13,
        fontFamily: FontName.RobotoRegular,
      },
      no_comment: {
        fontSize: '18px',
        color: color.color13,
        wordBreak: 'break-word'
      },
      subtitle_blue: {
        fontSize: '16px',
        color: color.color4
      },
      footer: {
        fontSize: '12px',
        color: color.color22,
        fontFamily: FontName.RobotoRegular,
      },
      footer_grey: {
        fontSize: '12px',
        color: color.color13,
      },
      footer_grey: {
        fontSize: '12px',
        color: color.color13,
        fontFamily: FontName.RobotoRegular,
      },
      text: {
        fontSize: '14px',
        color: color.color22,
        fontFamily: FontName.RobotoRegular,
      },
      text_grey: {
        fontSize: '14px',
        color: color.color13,
        fontFamily: FontName.RobotoRegular,
        wordBreak: 'break-word'
      },
      button: {
        textTransform: 'none',
      },
      no_data: {
        width: '100%',
        textAlign: 'center',
        fontSize: '18px',
        color: color.color13,
      },
      mediumBold: {
        fontWeight: '500'
      },
      blue_title: {
        fontWeight: 'bold',
        color: color.color4
      }
    },
  }))
  const handleTheme: HandleTheme = ({ color, theme }: { color?: Color; theme?: ThemeOptions }) => {
    if (color) { setColors(color) }
    if (theme) { setDefaultTheme(createTheme(theme)) }
  }

  return {
    theme: defaultTheme,
    color,
    setColors,
    handleTheme
  }
}
